import React, { useState, useEffect } from 'react';
import { displayService, screensService, groupsService } from '../services/api';
import DisplayPageModal from '../components/DisplayPageModal';
import SendToScreensModal from '../components/SendToScreensModal';

function DisplayPages({ user }) {
  const [pages, setPages] = useState([]);
  const [screens, setScreens] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [sendingPage, setSendingPage] = useState(null);
  const [editingPage, setEditingPage] = useState(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [pagesRes, screensRes, groupsRes] = await Promise.all([
        displayService.getAll(),
        screensService.getAll(),
        groupsService.getAll()
      ]);
      setPages(pagesRes.data);
      setScreens(screensRes.data);
      setGroups(groupsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingPage(null);
    setShowModal(true);
  };

  const handleEdit = (page) => {
    setEditingPage(page);
    setShowModal(true);
  };

  const handleDelete = async (pageId) => {
    if (!window.confirm('Supprimer cette page d\'affichage ?')) return;
    try {
      await displayService.delete(pageId);
      setMessage('✅ Page supprimée');
      setTimeout(() => setMessage(''), 3000);
      loadData();
    } catch (error) {
      setMessage('❌ Erreur: ' + (error.response?.data?.error || 'Erreur'));
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleDuplicate = async (page) => {
    try {
      await displayService.duplicate(page.id);
      setMessage('✅ Page dupliquée');
      setTimeout(() => setMessage(''), 3000);
      loadData();
    } catch (error) {
      setMessage('❌ Erreur: ' + (error.response?.data?.error || 'Erreur'));
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleSave = async (pageData) => {
    try {
      if (editingPage) {
        await displayService.update(editingPage.id, pageData);
        setMessage('✅ Page mise à jour');
      } else {
        await displayService.create(pageData);
        setMessage('✅ Page créée');
      }
      setShowModal(false);
      setTimeout(() => setMessage(''), 3000);
      loadData();
    } catch (error) {
      setMessage('❌ Erreur: ' + (error.response?.data?.error || 'Erreur'));
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleVideoUpload = async (pageId, file) => {
    const formData = new FormData();
    formData.append('video', file);
    try {
      await displayService.uploadVideo(pageId, formData);
      setMessage('✅ Vidéo uploadée');
      setTimeout(() => setMessage(''), 3000);
      loadData();
    } catch (error) {
      setMessage('❌ Erreur upload: ' + (error.response?.data?.error || 'Erreur'));
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleLogoUpload = async (pageId, file) => {
    const formData = new FormData();
    formData.append('logo', file);
    try {
      await displayService.uploadLogo(pageId, formData);
      setMessage('Logo uploade');
      setTimeout(() => setMessage(''), 3000);
      loadData();
    } catch (error) {
      setMessage('Erreur upload logo: ' + (error.response?.data?.error || 'Erreur'));
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleHeaderLogoUpload = async (pageId, file) => {
    const formData = new FormData();
    formData.append('logo', file);
    try {
      await displayService.uploadHeaderLogo(pageId, formData);
      setMessage('Logo en-tete uploade');
      setTimeout(() => setMessage(''), 3000);
      loadData();
    } catch (error) {
      setMessage('Erreur upload logo en-tete: ' + (error.response?.data?.error || 'Erreur'));
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleSendToScreen = async (page) => {
    const screenId = page.screen_id;
    if (!screenId) {
      setMessage('❌ Aucun écran assigné à cette page');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    const screen = screens.find(s => s.id === screenId);
    if (!screen) return;
    
    try {
      // 1. Mettre à jour default_page_id pour que l'écran affiche cette page
      await screensService.update(screenId, { default_page_id: page.id });
      // 2. Envoyer la commande refresh au Pi
      const displayUrl = `https://dynaview.dynatime.ch/display/${screen.pi_id}`;
      await screensService.sendCommand(screenId, 'change_url', { url: displayUrl });
      setMessage(`✅ Page "${page.name}" envoyée à ${screen.name}`);
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('❌ Erreur commande');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleSendToGroup = async (page) => {
    const groupId = page.group_id;
    if (!groupId) {
      setMessage('❌ Aucun groupe assigné à cette page');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    const group = groups.find(g => g.id === groupId);
    if (!group) return;
    
    // Envoyer à tous les écrans du groupe
    const groupScreens = screens.filter(s => s.group_id === groupId);
    for (const screen of groupScreens) {
      const displayUrl = `https://dynaview.dynatime.ch/display/${screen.pi_id}`;
      try {
        await screensService.sendCommand(screen.id, 'change_url', { url: displayUrl });
      } catch (err) {
        console.error(`Error sending to ${screen.name}:`, err);
      }
    }
    setMessage(`✅ Page envoyée au groupe ${group.name} (${groupScreens.length} écrans)`);
    setTimeout(() => setMessage(''), 3000);
  };

  const getPreviewUrl = (page) => {
    if (page.screen_id) {
      const screen = screens.find(s => s.id === page.screen_id);
      if (screen) return `/display/${screen.pi_id}`;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-screen">
          <div className="spinner"></div>
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Pages d'affichage</h1>
        <button onClick={handleCreate} className="btn-primary">
          + Nouvelle page
        </button>
      </div>

      {message && <div className="message-banner">{message}</div>}

      <div className="display-pages-list">
        {pages.length === 0 ? (
          <div className="empty-state">
            <p>Aucune page.</p>
          </div>
        ) : (
          [...pages].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map(page => (
            <div key={page.id} className={`dp-list-row ${page.is_active ? "" : "inactive"}`}>
              <div className="dp-list-name">
                <span className={`status-dot ${page.is_active ? "online" : "offline"}`}></span>
                <strong>{page.name}</strong>
              </div>
              <div className="dp-list-assign">
                {page.screen_name || page.group_name || <span style={{opacity:0.4}}>Non assignee</span>}
              </div>
              <div className="dp-list-info">
                {page.weather_enabled && <span>{page.weather_city}</span>}
                {page.clock_enabled && <span>Horloge</span>}
                {page.linkedin_enabled && <span>LinkedIn</span>}
                {page.daily_events_enabled && <span>Events</span>}
              </div>
              <div className="dp-list-actions">
                <button onClick={() => handleEdit(page)} className="btn-sm">Modifier</button>
                <button onClick={() => handleDuplicate(page)} className="btn-sm btn-warning" title="Dupliquer">⧉</button>
                {getPreviewUrl(page) && (
                  <button onClick={() => window.open(getPreviewUrl(page), "_blank")} className="btn-sm btn-info">Preview</button>
                )}
                <button onClick={() => setSendingPage(page)} className="btn-sm btn-success">Envoyer</button>
                <button onClick={() => handleDelete(page.id)} className="btn-sm btn-danger">x</button>
              </div>
            </div>
          ))
        )}
      </div>

      {sendingPage && (
        <SendToScreensModal
          page={sendingPage}
          screens={screens}
          onClose={() => setSendingPage(null)}
          onSuccess={(count) => {
            setMessage(`Envoye sur ${count} ecran(s)`);
            setTimeout(() => setMessage(''), 3000);
            loadData();
          }}
        />
      )}

      {showModal && (
        <DisplayPageModal
          page={editingPage}
          screens={screens}
          groups={groups}
          onSave={handleSave}
          onVideoUpload={handleVideoUpload}
          onLogoUpload={handleLogoUpload}
          onHeaderLogoUpload={handleHeaderLogoUpload}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}

export default DisplayPages;
